import { Popper } from "@mui/material";
import { EllipsisVertical } from "@procore/core-icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { renderActions } from "features/render";
import { SubtreesPopover } from "features/render/subtreesPopover";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { QUICK_ACCESS_TOOLBAR, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";
import { Toolbar } from "../Toolbar";
import { useToolsButtons } from "./useToolsButtons";
import { useViewToolbarButtons } from "./useViewToolbarButtons";
import { flyOutMenuHandler, TOOLS_BUTTON_IDS } from "./utils";

function FlyoutSeparator() {
    return (
        <hr
            style={{
                width: `${TOOLBAR_CONTAINER.WIDTH - TOOLBAR_CONTAINER.PADDING * 2}px`,
                margin: "4px 0",
                border: "none",
                borderTop: `1px solid ${TOOLBAR_COLORS.BORDER}`,
            }}
        />
    );
}

interface OverflowFlyoutToolbarProps {
    id: string;
    primaryToolbarId: string;
    onClose: () => void;
}

function OverflowFlyoutToolbar({ id, primaryToolbarId, onClose }: OverflowFlyoutToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const { buttons: viewButtons, subtreesBtnRef, isSubtreesPanelOpen } = useViewToolbarButtons();

    const handleToolSelect = useCallback(() => onCloseRef.current(), []);
    const { buttons: toolButtons, isMeasureActive, isSectionActive } = useToolsButtons(handleToolSelect);

    // In the flyout, tool buttons are only shown when NOT active (the active tool
    // is already displayed in CurrentModeToolbar), and never shown as active.
    const flyoutToolButtons = toolButtons.map((btn) => ({
        ...btn,
        state: {
            ...btn.state,
            active: false,
            visible: btn.config.id === TOOLS_BUTTON_IDS.MEASURE ? !isMeasureActive : !isSectionActive,
        },
    }));

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            ...viewButtons,
            {
                config: { id: "overflow-separator", icon: null, component: FlyoutSeparator },
                state: { active: false, disabled: false, visible: true },
            },
            ...flyoutToolButtons,
        ];

        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons }));

        const disposeMenuHandler = flyOutMenuHandler({
            flyoutMenuId: id,
            toolbarId: primaryToolbarId,
            close: () => onCloseRef.current(),
            shouldIgnoreClickAway: (event) =>
                event.target instanceof Element && Boolean(event.target.closest("#subtrees-popover")),
        });

        return () => {
            disposeMenuHandler();
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, primaryToolbarId, viewButtons, flyoutToolButtons]);

    return (
        <>
            <Toolbar id={id} variant="fly-out" />
            <SubtreesPopover
                open={isSubtreesPanelOpen}
                anchorEl={subtreesBtnRef.current}
                onClose={() => dispatch(renderActions.setSubtreesPanelOpen(false))}
            />
        </>
    );
}

interface OverflowToolbarProps {
    id?: string;
}

/**
 * Toolbar with a single overflow (ellipsis) button shown when a current-mode picker is active.
 * Opens a flyout with view tool buttons (camera, render modes, x-ray) plus the inactive
 * tool-switching buttons (measure/section).
 */
export function OverflowToolbar({ id = "overflow-toolbar" }: OverflowToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const ellipsisBtnRef = useRef<HTMLButtonElement>(null);
    const primaryToolbarId = `${id}-primary`;
    const flyoutId = `${id}-flyout`;

    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isVisible = activeToolMode !== ProcoreToolMode.Default;

    const handleClose = useCallback(() => {
        setIsMenuOpen(false);
        dispatch(renderActions.setSubtreesPanelOpen(false));
    }, [dispatch]);

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "overflow-toggle",
                    icon: <EllipsisVertical />,
                    onClick: () => setIsMenuOpen((open) => !open),
                    title: "More Tools",
                    ref: ellipsisBtnRef,
                },
                state: {
                    active: isMenuOpen,
                    disabled: false,
                    visible: isVisible,
                },
            },
        ];

        toolbarDispatch(toolbarsActions.addToolbar({ id: primaryToolbarId, buttons }));

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(primaryToolbarId));
        };
    }, [toolbarDispatch, primaryToolbarId, isMenuOpen, isVisible]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(primaryToolbarId, {
                "overflow-toggle": { active: isMenuOpen, visible: isVisible },
            }),
        );
    }, [toolbarDispatch, primaryToolbarId, isMenuOpen, isVisible]);

    useEffect(() => {
        if (!isVisible) {
            setIsMenuOpen(false);
        }
    }, [isVisible]);

    return (
        <>
            <Toolbar id={primaryToolbarId} />
            <Popper
                open={isMenuOpen}
                anchorEl={ellipsisBtnRef.current}
                placement="left-start"
                sx={{ zIndex: QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX }}
            >
                <OverflowFlyoutToolbar id={flyoutId} primaryToolbarId={primaryToolbarId} onClose={handleClose} />
            </Popper>
        </>
    );
}
